<script lang="ts" setup>
import moment from 'moment'
import Industry from '../Filter/Industry.vue'
import { useSearch } from '~/store/search'
import locales from '~/assets/data/locales.json'
import dateLocale from '~/assets/data/date-localization.json'
import type { NewPayload } from '~/models/Search'
import type { CompanyItem } from '~/models/Company'

interface Props {
  currUrl: {}
}

const props = defineProps<Props>()
const emit = defineEmits(['setMeta', 'tabTotal', 'currUrl', 'calculateOther'])
const route = useRoute()
const router = useRouter()
const { $config } = useNuxtApp()
const searchStore = useSearch()
const isLoading = ref(true)
const companyData = ref()
const newPayload = ref<NewPayload>(await searchStore.getPayload(route.params, isEmpty(props.currUrl) ? route.query : props.currUrl))
const locationList = ref()
const selLocation = ref(newPayload.value?.filter?.location || '')
const selIndustry = ref(newPayload.value?.filter?.industry || '')
const openLocation = ref(false)
const openIndustry = ref(false)
const emptyList = ['SHEIN', 'McDonald’s', 'Netflix']
const currPage = ref<number>(!isEmpty(route.query?.page) ? parseInt(route.query?.page.toString()) : 1)
const countryID = useGetSubdomain()
const totalPagination = ref(Math.ceil(companyData.value?.total / newPayload.value?.filter?.size))

// INITIAL FETCH
try {
  emit('calculateOther')
  companyData.value = {}
  isLoading.value = true
  const [result, locations] = await Promise.all([
    useAsyncData('get_company_search_list', () => searchStore.getCompany(newPayload.value?.filter, $config.public.SEARCHAPI_BASE_URL), { server: true }),
    useAsyncData('get_loc_comp_list', () => searchStore.getLoc('companies', newPayload.value?.filter), { server: true }),
  ])
  const rawCompany = result.data.value as { total: number; hits?: CompanyItem[] }
  if (!isEmpty(rawCompany) && rawCompany?.hits) {
    const companyList = {
      version1: rawCompany.hits.filter(company => company.employerVersion !== 2),
      version2: rawCompany.hits.filter(company => company.employerVersion === 2),
    }
    companyData.value = { company: companyList, total: rawCompany.total }
    emit('tabTotal', companyData.value?.total)
  }

  const rawLoc = locations.data.value
  if (rawLoc)
    locationList.value = rawLoc

  const newQuery = {
    st: 'companies',
    q: newPayload.value?.keyword || '',
    location: newPayload.value?.filter?.location || 'all',
    industry: newPayload.value?.filter?.industry || 'all',
    page: newPayload.value?.filter?.from || 1,
  }
  updateMeta(newQuery)
}
catch (error) {
  console.error('Error fetching data', error)
  showError({ message: 'Failed to load data', statusCode: 500 })
}

async function fetchCompany(newQuery: {}) {
  companyData.value = {}
  isLoading.value = true

  newPayload.value = await searchStore.getPayload(route.params, newQuery)
  selLocation.value = newPayload.value?.filter?.location || ''
  selIndustry.value = newPayload.value?.filter?.industry || ''

  const result = await searchStore.getCompany(newPayload.value?.filter, $config.public.SEARCHAPI_BASE_URL)
  const rawCompany = result as { total: number; hits?: CompanyItem[] }
  if (rawCompany?.hits) {
    const companyList = {
      version1: rawCompany.hits.filter(company => company.employerVersion !== 2),
      version2: rawCompany.hits.filter(company => company.employerVersion === 2),
    }
    companyData.value = { company: companyList, total: rawCompany.total }
    locationList.value = await searchStore.getLoc('companies', newPayload.value?.filter)
  }
  updateMeta(newQuery)
}

async function updateFilter(page: number, type = '') {
  // we have 3 type: null is for filters, reset coming from reset all btn, and other type that is the value of emptyList opts
  currPage.value = page
  openLocation.value = false
  openIndustry.value = false

  const newQuery = {
    st: 'companies',
    q: isEmpty(type) ? (isEmpty(newPayload.value?.keyword) ? '' : newPayload.value?.keyword) : '',
    location: isEmpty(type) ? selLocation.value || 'all' : 'all',
    industry: isEmpty(type) ? selIndustry.value || 'all' : 'all',
    page: isEmpty(type) ? page : 1,
  }
  router.replace({ path: '/search', query: newQuery })
  if (newPayload.value?.searchType !== 'longtail')
    fetchCompany(newQuery)
  else
    updateMeta(newQuery)
}

function updateMeta(newQuery: {}) {
  const countryName = useTextCapitalize(locales.find(item => item.region === countryID)?.label || '')
  const metaLocation = isEmpty(newPayload.value?.filter?.location) ? `${countryName}` : `${useTextCapitalize(newPayload.value?.filter?.location)}, ${countryName}`
  const monthID = dateLocale[0]?.months[parseInt(moment().format('M')) - 1]
  const monthMY = dateLocale[1]?.months[parseInt(moment().format('M')) - 1]
  const metaTitle = ref('')
  const metaDesc = ref('')
  const metaCanon = ref('')
  const metaRobot = companyData?.value?.total > 0 ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' : 'nofollow, noindex'

  if (useGetSubdomain() === 'id') {
    metaTitle.value = `Temukan ${companyData?.value?.total} Perusahaan ${useTextCapitalize(newPayload.value?.keyword ? newPayload.value?.keyword.replaceAll('-', ' ') : '')} di ${metaLocation} | ${monthID} ${moment().format('YYYY')} | Epicareer`
    metaDesc.value = `Temukan ${companyData?.value?.total} perusahaan ${useTextCapitalize(newPayload.value?.keyword ? newPayload.value?.keyword.replaceAll('-', ' ') : '')} di ${metaLocation}. Cari tau budaya kerja dan rata-rata gaji karyawan ${useTextCapitalize(newPayload.value?.keyword ? newPayload.value?.keyword.replaceAll('-', ' ') : '')} di Epicareer!`
  }
  else if (useGetSubdomain() === 'my') {
    metaTitle.value = `Temukan ${companyData?.value?.total} Syarikat ${useTextCapitalize(newPayload.value?.keyword ? newPayload.value?.keyword.replaceAll('-', ' ') : '')} di ${metaLocation} | ${monthMY} ${moment().format('YYYY')} | Epicareer`
    metaDesc.value = `Temukan ${companyData?.value?.total} syarikat ${useTextCapitalize(newPayload.value?.keyword ? newPayload.value?.keyword.replaceAll('-', ' ') : '')} di ${metaLocation}. Cari tahu budaya kerja dan purata gaji pekerja ${useTextCapitalize(newPayload.value?.keyword ? newPayload.value?.keyword.replaceAll('-', ' ') : '')} di Epicareer!`
  }
  else {
    metaTitle.value = `${companyData?.value?.total} ${useTextCapitalize(newPayload.value?.keyword ? newPayload.value?.keyword.replaceAll('-', ' ') : '')} Companies in ${metaLocation} | ${moment().format('MMMM')} ${moment().format('YYYY')} | Epicareer`
    metaDesc.value = `Discover ${companyData?.value?.total} ${useTextCapitalize(newPayload.value?.keyword ? newPayload.value?.keyword.replaceAll('-', ' ') : '')} in ${metaLocation}. Explore ${useTextCapitalize(newPayload.value?.keyword ? newPayload.value?.keyword.replaceAll('-', ' ') : '')} work culture and average salaries on Epicareer!`
  }
  metaCanon.value = route.path

  emit('setMeta', { title: metaTitle.value, desc: metaDesc.value, canon: metaCanon.value, robots: metaRobot })
  emit('tabTotal', companyData.value?.total)
  if (!isEmpty(newQuery))
    emit('currUrl', newQuery)

  isLoading.value = false
  totalPagination.value = Math.ceil(companyData.value?.total / newPayload.value?.filter?.size)
}
</script>

<template>
  <div v-if="route.query?.st === 'companies'">
    <div v-if="isLoading" class="grid gap-y-4 lg:gap-y-6">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <CompaniesCompanyCardV1 v-for="idx in 12" :key="idx" :animation="true" class="h-full" />
      </div>
    </div>
    <div v-if="!isLoading" class="grid gap-y-4">
      <div v-if="companyData?.total > 0">
        <h1 class="text-3xl lg:text-5xl font-bold mb-2 lg:mb-4">
          {{ useTextCapitalize(newPayload?.keyword.replaceAll('-', ' ')) }} Companies
        </h1>
        <span class="text-sm text-gray-600">
          Found {{ useCurrencyFormat(null, companyData?.total, null, false, true, false) }} Companies {{ newPayload?.keyword ? `related to ”${useTextCapitalize(newPayload?.keyword.replaceAll('-', ' '))}”` : '' }}
        </span>
      </div>

      <!-- Filter Section -->
      <ClientOnly>
        <div class="z-20 snap snap-x snap-mandatory flex pb-2 lg:pb-0 max-w-screen">
          <div class="snap-start block lg:hidden mr-3">
            <!-- mobile filter -->
            <SearchFilterMobileCompany
              :list-location="locationList"
              :ori-industry="selIndustry"
              :ori-location="selLocation"
              @update:location="item => { selLocation = item; updateFilter(1) }"
              @update:industry="item => { selIndustry = item; updateFilter(1) }"
              @open-location="val => { openLocation = val }"
              @open-industry="val => { openIndustry = val }"
            />
          </div>

          <div class="snap-start hidden lg:block mr-3">
            <!-- location filter -->
            <SearchFilterLocation :is-open="openLocation" :list="locationList" :ori-location="selLocation" @update:model-value="item => { selLocation = item; updateFilter(1) }" @update:open="val => { openLocation = val }" />
          </div>

          <!-- industry filter -->
          <div class="snap-start hidden lg:block mr-3">
            <SearchFilterIndustry :is-open="openIndustry" :ori-industry="selIndustry" @update:model-value="val => { selIndustry = val, updateFilter(1) }" @update:open="val => { openIndustry = val }" />
          </div>

          <!-- reset all btn -->
          <div v-if="!isEmpty(selIndustry) && !isEmpty(selLocation)" class="text-[#5843E5] text-sm cursor-pointer font-semibold my-auto" @click="updateFilter(1, 'reset')">
            Reset All
          </div>
        </div>
      </ClientOnly>

      <!-- Card Section -->
      <div v-if="!isEmpty(companyData?.company?.version1) || !isEmpty(companyData?.company?.version2)" class="grid gap-y-10 lg:mt-2">
        <div v-if="!isEmpty(companyData.company?.version2)" class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
          <CompaniesCompanyCardV2 v-for="(companyItem, idx) in companyData.company?.version2" :key="idx" :item="companyItem" />
        </div>
        <div v-if="!isEmpty(companyData.company?.version1)" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <CompaniesCompanyCardV1 v-for="(companyItem, idx) in companyData.company?.version1" :key="idx" impression-source="search-job" :item="companyItem" class="h-full" />
        </div>

        <div class="pl-0 flex-1 flex justify-end">
          <SearchPagination :curr-page="currPage" :total-pages="totalPagination" @change-page="value => updateFilter(value)" />
        </div>
      </div>
    </div>
    <div v-if="!isLoading && (companyData?.company?.version1.length + companyData?.company?.version1.length) === 0" class="grid gap-y-4 mt-10">
      <Icon name="material-symbols:explore-outline" class="w-51 h-51 text-[#5843E5] mx-auto" />
      <div class="text-xl font-bold mx-auto text-center">
        Sorry we couldn’t find Companies {{ !isEmpty(newPayload?.keyword) ? `Related to “${useTextCapitalize(useTextToParam(newPayload?.keyword.toString(), true))}”` : '' }}  {{ useTextCapitalize((isEmpty(selLocation) || selLocation === 'all') ? 'in everywhere' : selLocation.toString().replace(/-/g, ' ')) }}
      </div>
      <div class="text-sm mx-auto text-center text-gray-600">
        Please looking for other terms..<br>
        or you can try this instead
      </div>
      <div v-for="(item, idx) in emptyList" :key="idx" class="flex items-center mx-auto text-xs text-[#5843E5] cursor-pointer" @click="updateFilter(1, item)">
        <Icon name="fluent-emoji-flat:fire" class="w-4 h-4 mr-1" />
        {{ item }}
        <Icon name="material-symbols:arrow-outward" class="w-4 h-4 ml-1" />
      </div>
    </div>
    <SearchArticlesSection :keyword="newPayload?.keyword" />
  </div>
</template>
